import type { LoadableComponent } from '@loadable/component';
import loadable from '@loadable/component';

import type { RouteConfigUtil } from 'helpers/RouteUtils';
import {
  alwaysIgnoreScrollToTop,
  shouldAccountIgnoreScrollToTop,
  shouldCheckoutIgnoreScrollToTop,
  shouldSearchIgnoreScrollToTop
} from 'helpers/RouteUtils';
import marketplace from 'cfg/marketplace.json';
import App from 'containers/App';
import { getRouteConfig } from 'routes/getRouteConfig';

export const AppNode = App;

export enum RouteType {
  ROUTE,
  REDIRECT,
  ACCOUNT_ROUTE
}

export interface RouteConfig {
  path: string;
  routeType: RouteType;
  exact?: boolean;
  component?: any;
  to?: string;
  suppressScrollOnRouteChange?: (prevRouterProps: RouteConfigUtil, newRouterProps: RouteConfigUtil) => boolean;
}

const {
  pdp: { egcUrl },
  account: { vipDashboardUrl },
  returns: { handlesReturnShipping }
} = marketplace;

const routeHelpers = {
  shouldCheckoutIgnoreScrollToTop,
  alwaysIgnoreScrollToTop,
  shouldAccountIgnoreScrollToTop,
  shouldSearchIgnoreScrollToTop
};

// See https://webpack.js.org/api/module-methods/#magic-comments
// These webpack chunk names must either match the component class name, or the class itself must define `static webpackChunkName`
// for CSS splitting to work without users seeing a FOUC.  These chunksNames are then referenced via the produced webpackStats object.
const components: { [key: string]: LoadableComponent<any> } = {
  LazyAccount: loadable(() => import(/* webpackChunkName: "Account" */ 'containers/account/Account')),
  LazyAddPayment: loadable(() => import(/* webpackChunkName: "AddPayment" */ 'containers/account/AddPayment')),
  LazyAddReview: loadable(() => import(/* webpackChunkName: "AddReview" */ 'containers/AddReview')),
  LazyAddShipping: loadable(() => import(/* webpackChunkName: "AddShipping" */ 'containers/account/AddShipping')),
  LazyAsinStockIdProductDetail: loadable(() => import(/* webpackChunkName: "AsinStockIdProductDetail" */ 'containers/AsinStockIdProductDetail')),
  LazyAsinAddReview: loadable(() => import(/* webpackChunkName: "AsinAddReview" */ 'containers/AsinAddReview')),
  LazyBrokenPage: loadable(() => import(/* webpackChunkName: "BrokenPage" */ 'components/error/BrokenPage')),
  LazyCostlyCustomerPage: loadable<any>(() => import(/* webpackChunkName: "CostlyCustomerPage" */ 'components/error/CostlyCustomerPage')),
  LazyCart: loadable(() => import(/* webpackChunkName: "Cart", webpackPrefetch: 30  */ 'containers/Cart')),
  LazyCheckout: loadable(() => import(/* webpackChunkName: "Checkout" , webpackPrefetch: 20 */ 'containers/checkout/Checkout')),
  LazyCreateLabel: loadable(() => import(/* webpackChunkName: "CreateLabel" */ 'containers/CreateLabel')),
  LazyEditPayment: loadable(() => import(/* webpackChunkName: "EditPayment" */ 'containers/account/EditPayment')),
  LazyEditShipping: loadable(() => import(/* webpackChunkName: "EditShipping" */ 'containers/account/EditShipping')),
  LazyFederatedLogin: loadable(() => import(/* webpackChunkName: "FederatedLoginPage" */ 'containers/account/FederatedLoginPage')),
  LazyHmdSurvey: loadable(() => import(/* webpackChunkName: "HmdSurvey" */ 'containers/HmdSurvey')),
  LazySubscriptions: loadable(() => import(/* webpackChunkName: "Subscriptions" */ 'containers/Subscriptions')),
  LazyCollections: loadable(() => import(/* webpackChunkName: "Collections" */ 'containers/account/Collections')),
  LazyDeleteAccount: loadable(() => import(/* webpackChunkName: "DeleteAccount" */ 'containers/account/DeleteAccount')),
  LazyCollection: loadable(() => import(/* webpackChunkName: "Collection" */ 'containers/account/Collection')),
  LazyLanding: loadable(() => import(/* webpackChunkName: "Landing", webpackPrefetch: 50 */ 'containers/Landing')),
  LazyOrder: loadable(() => import(/* webpackChunkName: "Order" */ 'containers/account/Order')),
  LazyCancelOrder: loadable(() => import(/* webpackChunkName: "CancelOrder" */ 'containers/account/CancelOrder')),
  LazyOrderHistory: loadable(() => import(/* webpackChunkName: "OrderHistory" */ 'containers/account/OrderHistory')),
  LazyOrderConfirmation: loadable(() => import(/* webpackChunkName: "OrderConfirmation" */ 'containers/checkout/OrderConfirmation')),
  LazyPayments: loadable(() => import(/* webpackChunkName: "Payments" */ 'containers/account/Payments')),
  LazyProductDetail: loadable(() => import(/* webpackChunkName: "ProductDetail", webpackPrefetch: 40 */ 'containers/ProductDetail')),
  LazyProductReviews: loadable(() => import(/* webpackChunkName: "ProductReviews" */ 'containers/ProductReviews')),
  LazyReturns: loadable(() => import(/* webpackChunkName: "Returns" */ 'containers/account/Returns')),
  LazyCancelReturn: loadable(() => import(/* webpackChunkName: "CancelReturn" */ 'components/account/Returns/CancelReturn/CancelReturn')),
  LazyReturnAll: loadable(() => import(/* webpackChunkName: "ReturnAll" */ 'containers/account/ReturnAll')),
  LazyReturnLabel: loadable(() => import(/* webpackChunkName: "ReturnLabel" */ 'containers/account/ReturnLabel')),
  LazyReturnInfo: loadable(() => import(/* webpackChunkName: "MyReturnDetails" */ 'containers/account/MyReturnDetails')),
  LazyVipOptin: loadable(() => import(/* webpackChunkName: "VipProgramOptin" */ 'containers/account/VipProgramOptin')),
  LazyVipOptinAck: loadable(() => import(/* webpackChunkName: "VipJustEnrolledFromToken" */ 'containers/account/VipJustEnrolledFromToken')),
  LazyExchange: loadable(() => import(/* webpackChunkName: "Exchanges" */ 'components/exchanges/Exchanges')),
  LazyRouteNotFound: loadable(() => import(/* webpackChunkName: "RouteNotFound" */ 'containers/RouteNotFound')),
  LazySearch: loadable(() => import(/* webpackChunkName: "StandardSearch" */ 'containers/StandardSearch')),
  LazyShipping: loadable(() => import(/* webpackChunkName: "Shipping" */ 'containers/account/Shipping')),
  LazyShipmentTracking: loadable(() => import(/* webpackChunkName: "ShipmentTracking" */ 'containers/account/ShipmentTracking')),
  LazySymphonyPreview: loadable(() => import(/* webpackChunkName: "SymphonyPreview" */ 'containers/SymphonyPreview')),
  LazyTaxonomyBrand: loadable(() => import(/* webpackChunkName: "TaxonomyBrandPage" */ 'containers/TaxonomyBrandPage')),
  LazyWildCard: loadable(() => import(/* webpackChunkName: "WildCard", webpackPrefetch: 60 */ 'containers/WildCard')),
  LazyMyOrders: loadable(() => import(/* webpackChunkName: "MyOrders" */ 'containers/account/MyOrders')),
  LazyMyReturns: loadable(() => import(/* webpackChunkName: "MyReturns" */ 'containers/account/MyReturns')),
  LazyMyOrderDetails: loadable(() => import(/* webpackChunkName: "MyOrderDetails" */ 'containers/account/MyOrderDetails')),
  LazyItemDetails: loadable(() => import(/* webpackChunkName: "ItemDetails" */ 'containers/account/ItemDetails')),
  LazyReturnFlowReturnItem: loadable(
    () => import(/* webpackChunkName: "ReturnFlowReturnItem" */ 'components/account/MyReturnsFlow/ReturnFlowReturnItem')
  ),
  LazyCancelMyReturn: loadable(() => import(/* webpackChunkName: "CancelMyReturn" */ 'containers/account/CancelMyReturn')),
  LazyCancelMyOrder: loadable(() => import(/* webpackChunkName: "CancelMyOrder" */ 'containers/account/CancelMyOrder'))
};

const routesConfig = getRouteConfig(egcUrl, vipDashboardUrl, handlesReturnShipping)
  .map(rc => {
    let ret: RouteConfig;
    if (rc.routeType === 'ROUTE' || rc.routeType === 'ACCOUNT_ROUTE') {
      ret = {
        routeType: rc.routeType === 'ROUTE' ? RouteType.ROUTE : RouteType.ACCOUNT_ROUTE,
        path: rc.path,
        component: components[rc.component || '']
      };
      if (rc.exact !== undefined) {
        ret.exact = rc.exact;
      }
      if (rc.suppressScrollOnRouteChange !== undefined) {
        ret.suppressScrollOnRouteChange = (routeHelpers as { [key: string]: any })[rc.suppressScrollOnRouteChange || ''];
        if (!ret.suppressScrollOnRouteChange) {
          throw new Error(`suppressScrollOnRouteChange '${rc.suppressScrollOnRouteChange}' not found`);
        }
      }
      if (!ret.component) {
        throw new Error(`component '${rc.component}' not found`);
      }
    } else if (rc.routeType === 'REDIRECT') {
      ret = {
        routeType: RouteType.REDIRECT,
        path: rc.path,
        to: rc.to
      };
    } else {
      return;
    }
    return ret;
  })
  .filter(e => !!e) as RouteConfig[];

export default routesConfig;
